import { useCallback, useRef } from 'react'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import {
  STREAM_EVENT,
  TOOL_NAME,
  type ItineraryUpdate,
  type EnrichmentCompleteEvent,
  type HotelSearchResultsData,
  type ExperienceSearchResultsData,
  type HotelSearchErrorData,
  type ExperienceSearchErrorData,
  type ChatUiActionData,
} from 'tripPlanner/api/types'
import { assertUnreachable } from 'tripPlanner/utils'
import { parseStreamEvent, type StreamEvent } from 'tripPlanner/utils/streamEventParser'
import { trackFirstToken, trackProgressiveUiPartFailed } from 'tripPlanner/utils/streamAnalytics'
import type { StreamMachineEvent } from './useStreamMachine'
import {
  applyProgressiveUiPartEvent,
  createStreamedUiPartsState,
  resetStreamedUiPartsState,
} from './streamedUiParts'

/** Throttle interval for CHUNK_ACTIVITY events sent to the machine. The stall
 *  timer (10s) uses reenter: true which cancels/restarts on each event — sending
 *  at RAF rate (~60Hz) would create unnecessary XState overhead. 5s gives plenty
 *  of margin below the 10s threshold. */
const CHUNK_ACTIVITY_THROTTLE_MS = 5_000

// ── Types ──────────────────────────────────────────────────────────────

interface UseStreamEventProcessorArgs {
  // Machine — single dispatch target for all state transitions
  send: (event: StreamMachineEvent) => void
  // Overlay state setters
  updateStreamingMessageParts: (parts: Array<MessagePart>) => void
  updateStreamingMessageMetadata: (metadata: { oaiConversationId?: string; rawContent?: string }) => void
  // Domain callbacks (wired to event bus by provider)
  onItineraryUpdates?: (chatId: number, updates: Array<ItineraryUpdate>) => void
  onEnrichmentComplete?: (chatId: number, event: EnrichmentCompleteEvent) => void
  onHotelSearchResults?: (data: HotelSearchResultsData, generation: number) => void
  onExperienceSearchResults?: (data: ExperienceSearchResultsData, generation: number) => void
  onHotelSearchError?: (data: HotelSearchErrorData, generation: number) => void
  onExperienceSearchError?: (data: ExperienceSearchErrorData, generation: number) => void
  onUiAction?: (data: ChatUiActionData) => void
  // Generation check — returns true if the given generation is still current
  isLatestGeneration: (generation: number) => boolean
  // Chat ID ref for trackFirstToken (chatId may not be in scope at event time)
  chatIdRef: React.RefObject<number | undefined>
}

export interface UseStreamEventProcessorReturn {
  /** Create handlers bound to a specific generation. Call once per stream. */
  createHandlers: (generation: number, assistantMessageId: string) => StreamHandlers
  resetBuffer: () => void
}

export interface StreamHandlers {
  processJsonlChunk: (rawChunk: string, currentChatId: number) => void
  flushTrailingBuffer: (currentChatId: number) => void
  /** Cancel any pending RAF callback. Call on stream end or unmount. */
  dispose: () => void
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useStreamEventProcessor({
  send,
  updateStreamingMessageParts,
  updateStreamingMessageMetadata,
  onItineraryUpdates,
  onEnrichmentComplete,
  onHotelSearchResults,
  onExperienceSearchResults,
  onHotelSearchError,
  onExperienceSearchError,
  onUiAction,
  isLatestGeneration,
  chatIdRef,
}: UseStreamEventProcessorArgs): UseStreamEventProcessorReturn {
  const streamBufferRef = useRef<string>('')

  const createHandlers = useCallback((thisGeneration: number, assistantMessageId: string): StreamHandlers => {
    let pendingRafId: number | undefined = undefined
    let pendingParts: Array<MessagePart> | undefined
    let accumulatedParts: Array<MessagePart> = []
    const streamedUiPartsState = createStreamedUiPartsState()
    let hasDispatchedFirstContent = false
    let isAwaitingModelResponse = false
    let lastChunkActivityTime = 0

    const dispatchModelResponseStarted = (normalisedParts: Array<MessagePart>) => {
      if (!isAwaitingModelResponse || normalisedParts.length === 0) return

      isAwaitingModelResponse = false
      send({ type: 'MODEL_RESPONSE_STARTED' })
    }

    const dispatchFirstContent = (normalisedParts: Array<MessagePart>) => {
      if (!hasDispatchedFirstContent && normalisedParts.length > 0) {
        hasDispatchedFirstContent = true
        send({ type: 'FIRST_CHUNK' })
        if (chatIdRef.current !== undefined) {
          trackFirstToken(chatIdRef.current)
        }
      }
    }

    const dispatchChunkActivity = () => {
      const now = Date.now()
      if (now - lastChunkActivityTime < CHUNK_ACTIVITY_THROTTLE_MS) return

      lastChunkActivityTime = now
      send({ type: 'CHUNK_ACTIVITY' })
    }

    const schedulePendingPartsFlush = () => {
      if (pendingRafId !== undefined) return

      pendingRafId = requestAnimationFrame(() => {
        pendingRafId = undefined
        if (!isLatestGeneration(thisGeneration)) return

        const partsToApply = pendingParts ?? []
        pendingParts = undefined
        updateStreamingMessageParts(partsToApply)
      })
    }

    const applyPartsSnapshot = (parts: Array<MessagePart>) => {
      if (!isLatestGeneration(thisGeneration)) return

      resetStreamedUiPartsState(streamedUiPartsState)
      accumulatedParts = parts.map(p => ({ ...p }))
      pendingParts = parts
      dispatchFirstContent(parts)
      dispatchModelResponseStarted(parts)

      schedulePendingPartsFlush()
    }

    const applyTokenDelta = (text: string) => {
      if (!isLatestGeneration(thisGeneration)) return

      // Mutate in place — safe because RAF boundary creates new array ref for React
      const lastPart = accumulatedParts[accumulatedParts.length - 1]
      if (lastPart?.type === 'text') {
        lastPart.content += text
      } else {
        accumulatedParts.push({ type: 'text', content: text })
      }

      dispatchFirstContent(accumulatedParts)
      dispatchModelResponseStarted(accumulatedParts)
      pendingParts = [...accumulatedParts]
      schedulePendingPartsFlush()
    }

    const scheduleAccumulatedPartsApply = () => {
      dispatchFirstContent(accumulatedParts)
      dispatchModelResponseStarted(accumulatedParts)
      pendingParts = [...accumulatedParts]
      schedulePendingPartsFlush()
    }

    const applyUiPartEvent = (
      event:
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_STARTED }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_PATCH }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_COMPLETED }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_FAILED }>,
    ) => {
      if (!isLatestGeneration(thisGeneration)) return

      const result = applyProgressiveUiPartEvent({
        event,
        expectedMessageId: assistantMessageId,
        parts: accumulatedParts,
        state: streamedUiPartsState,
        onPartFailed: (uiType, reason) => {
          trackProgressiveUiPartFailed(chatIdRef.current, uiType, reason)
        },
      })
      if (result.changed) {
        accumulatedParts = result.parts
        scheduleAccumulatedPartsApply()
      }
    }

    /** Apply parts synchronously — used for MESSAGE_END to ensure the overlay
     *  has the final complete parts before stream cleanup runs. */
    const applyFinalParts = (parts: Array<MessagePart>) => {
      if (!isLatestGeneration(thisGeneration)) return

      resetStreamedUiPartsState(streamedUiPartsState)
      dispatchFirstContent(parts)
      dispatchModelResponseStarted(parts)
      updateStreamingMessageParts(parts)
    }

    // Shared handler for a single parsed stream event. Uses an exhaustive
    // switch so TypeScript errors at compile time if a new StreamEvent
    // variant is added but not handled here — preventing the class of bug
    // where one code-path silently drops an event type.
    const handleStreamEvent = (event: StreamEvent, currentChatId: number) => {
      switch (event.event) {
        case STREAM_EVENT.ITINERARY_UPDATE_STARTED:
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'ITINERARY_UPDATE_START' })
          break
        case STREAM_EVENT.ITINERARY_UPDATES:
          onItineraryUpdates?.(currentChatId, event.updates)
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'ITINERARY_UPDATE_COMPLETE' })
          break
        case STREAM_EVENT.ENRICHMENT_COMPLETE:
          onEnrichmentComplete?.(currentChatId, event.data)
          break
        case STREAM_EVENT.CHAT_LOADING_STATE:
          if (!isLatestGeneration(thisGeneration)) break
          if (event.data.phase === 'start') {
            isAwaitingModelResponse = false
            send({ type: 'TOOL_LOADING_START', callId: event.data.callId, key: event.data.key })
          } else {
            send({ type: 'TOOL_LOADING_COMPLETE', callId: event.data.callId })
          }
          break
        case STREAM_EVENT.HOTEL_SEARCH_RESULTS:
          onHotelSearchResults?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS:
          onExperienceSearchResults?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.HOTEL_SEARCH_ERROR:
          onHotelSearchError?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.EXPERIENCE_SEARCH_ERROR:
          onExperienceSearchError?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.CHAT_UI_ACTION:
          if (!isLatestGeneration(thisGeneration)) break
          onUiAction?.(event.data)
          break
        case STREAM_EVENT.CONTENT_COMPLETE:
          isAwaitingModelResponse = false
          send({ type: 'CONTENT_COMPLETE' })
          break
        case STREAM_EVENT.UI_PART_STARTED:
        case STREAM_EVENT.UI_PART_PATCH:
        case STREAM_EVENT.UI_PART_COMPLETED:
        case STREAM_EVENT.UI_PART_FAILED:
          if (!isLatestGeneration(thisGeneration)) break
          dispatchChunkActivity()
          applyUiPartEvent(event)
          break
        case STREAM_EVENT.MESSAGE_START:
          // With client-generated UUIDs, MESSAGE_START is just confirmation
          break
        case STREAM_EVENT.PARTS_UPDATE: {
          // Throttled — unthrottled causes 60Hz XState transitions (reenter: true on stall timer)
          dispatchChunkActivity()
          applyPartsSnapshot(event.parts)
          break
        }
        case STREAM_EVENT.MESSAGE_END: {
          // Apply final parts synchronously so the overlay has the complete
          // data (including UI components like cards/pills) before stream
          // cleanup runs. RAF batching can drop these if a parts_update
          // already scheduled a pending frame.
          applyFinalParts(event.parts)
          isAwaitingModelResponse = false
          send({ type: 'MESSAGE_END' })

          if (event.oaiConversationId || event.rawContent) {
            updateStreamingMessageMetadata({
              oaiConversationId: event.oaiConversationId,
              rawContent: event.rawContent,
            })
          }
          break
        }
        case STREAM_EVENT.STATUS: {
          if (!isLatestGeneration(thisGeneration)) break

          const { data } = event
          switch (data.phase) {
            case 'reasoning':
              isAwaitingModelResponse = true
              send({ type: 'STATUS', phase: 'reasoning', summaries: data.summaries })
              break
            case 'tool_calling':
              // Skip non-user-facing tools (view switches happen instantly, no loading label needed)
              isAwaitingModelResponse = false
              if (data.tool !== TOOL_NAME.SET_TRIP_PLANNER_VIEW) {
                send({ type: 'STATUS', phase: 'tool_calling', tool: data.tool, args: data.args })
              } else {
                send({ type: 'MODEL_RESPONSE_STARTED' })
              }
              break
            default:
              assertUnreachable(data)
          }
          break
        }
        case STREAM_EVENT.HEARTBEAT:
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'HEARTBEAT' })
          break
        case STREAM_EVENT.TOKEN_BATCH: {
          // Throttled — unthrottled causes 60Hz XState transitions (reenter: true on stall timer)
          dispatchChunkActivity()
          applyTokenDelta(event.text)
          break
        }
        default:
          assertUnreachable(event)
      }
    }

    const processJsonlChunk = (rawChunk: string, currentChatId: number) => {
      streamBufferRef.current += rawChunk
      const lines = streamBufferRef.current.split('\n')
      streamBufferRef.current = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const event = parseStreamEvent(trimmed)
        if (event) {
          handleStreamEvent(event, currentChatId)
        } else {
          console.warn('[trip-chat] Failed to parse stream event:', trimmed.substring(0, 200))
        }
      }
    }

    const flushTrailingBuffer = (currentChatId: number) => {
      const trailing = streamBufferRef.current.trim()
      if (trailing) {
        const event = parseStreamEvent(trailing)
        if (event) {
          handleStreamEvent(event, currentChatId)
        } else {
          console.warn('[trip-chat] Failed to parse trailing buffer:', trailing.substring(0, 200))
        }
      }
      streamBufferRef.current = ''
    }

    const dispose = () => {
      if (pendingRafId !== undefined) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = undefined
      }
      pendingParts = undefined
      accumulatedParts = []
      resetStreamedUiPartsState(streamedUiPartsState)
      isAwaitingModelResponse = false
    }

    return { processJsonlChunk, flushTrailingBuffer, dispose }
  }, [
    send,
    isLatestGeneration,
    onItineraryUpdates,
    onEnrichmentComplete,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    chatIdRef,
  ])

  const resetBuffer = useCallback(() => {
    streamBufferRef.current = ''
  }, [])

  return { createHandlers, resetBuffer }
}
